import React from 'react'
import { AsyncState, fromJSX, nothing, Updater, Widget } from 'widgets-for-react'
import { LinkLayout } from '../../../../../Shared/Frontend/utils/links'
import { pushDatalayer } from '../../../seo/datalayer'
import { CMSLoaderState, PageData } from '../../cmsPage.state'
import { State } from '../../cmsPage.widget'
import { SharedBlockLayout } from '../../modernBlocks/blocks/shared/sharedBlock.layout'
import { LinkGridBlockLayout } from '../../modernBlocks/blocks/gridBlock/gridBlock.layout'
import { convertTitleForBackground, unEntity } from '../../utils/html'

// ----- Default

const PageHeaderLayout = (currentPageData: PageData, state: State): Widget<Updater<State>> => {
  if ((currentPageData.header.kind != "normal" && currentPageData.header.kind != "fullscreen")) return nothing()

  const { image, imageSource, imageSize, linkPrimary, linkSecondary, pageIntro, pageTitle, publishDate } = currentPageData.header

  return fromJSX((setState) => (
    <>
      {imageSize == 'large'
        ? <>
            <header className="pageheader ph--image-full">
              <div className='ph__inner ls-container--l'>
                <div className='ph__content'>
                  <div className='ph__content-inner'>
                    {pageTitle && (
                      <h1 className='ph__title title--l title--bg' dangerouslySetInnerHTML={{ __html: unEntity(convertTitleForBackground(pageTitle)) }} />
                    )}
                  </div>
                </div>

                <div className="ph__visual ls-inner-container--m">
                  <div className='ph__visual-inner fit-picture'>
                    {image && imageSource &&
                      <SharedBlockLayout.ModernImagePicture
                        pictureKey='ph'
                        image={image}
                        sources={imageSource}
                        lazyload={false}
                        priority='high'
                        classNames="ph__image"
                        pictureClassNames='ph__picture'
                        imageProportions={{
                          width: '640',
                          height: '500',
                          sizes: '100vw'
                        }}
                      />
                    }
                  </div>
                </div>
              </div>
            </header>
          </>
        : <>
          <header className={`pageheader ph--${image ? 'image' : 'text'} ls-container--l ls-gutter`}>
            <div className='ph__inner ls-container--l'>
              <div className='ph__content'>
                <div className='ph__content-inner'>
                  {publishDate && (
                    <time dateTime={publishDate.toISOString()} className='ph__meta text--s'>{publishDate.toLocaleString("nl-NL", {day: 'numeric', month: 'long', year: 'numeric'})}</time>
                  )}

                  {pageTitle && (
                    <h1
                      className='ph__title title--l'
                      dangerouslySetInnerHTML={{ __html: pageTitle }}
                    />
                  )}

                  {pageIntro ? (
                    typeof pageIntro == 'string' ? (
                      <p className='ph__intro' dangerouslySetInnerHTML={{ __html: pageIntro }} />
                    ) : (
                      <p className='ph__intro'>{pageIntro}</p>
                    )
                  ) : (
                    <></>
                  )}

                  {(linkPrimary || linkSecondary) ? (
                    <div className='ph__actions ls-clickables'>
                      {linkPrimary && (
                        <div className="ph__action ls-clickable">
                          <LinkLayout
                            link={linkPrimary}
                            className='ph__button button button--primary button--on-darkred'
                            onClick={() => {
                              pushDatalayer({
                                event: 'event.gtm',
                                category: 'pageheader-cta',
                                action: 'primary-' + linkPrimary.title,
                                label: window.location.href
                              })
                            }}
                          >
                            <span className='button__label'>{linkPrimary.title}</span>
                          </LinkLayout>
                        </div>
                      )}
                      {linkSecondary && (
                        <div className="ph__action ls-clickable">
                          <LinkLayout
                            link={linkSecondary}
                            className='ph__link button button--secondary button--on-darkred'
                            onClick={() => {
                              pushDatalayer({
                                event: 'event.gtm',
                                category: 'pageheader-cta',
                                action: 'secondary-' + linkSecondary.title,
                                label: window.location.href
                              })
                            }}
                          >
                            <span className="button__label">{linkSecondary.title}</span>
                          </LinkLayout>
                        </div>
                      )}
                    </div>
                  ) : (
                    <></>
                  )}
                </div>
              </div>

              {image && <div className="ph__visual">
                <div className='ph__visual-inner fit-picture'>
                  <SharedBlockLayout.ModernImageSrcSet
                    image={image}
                    lazyload={false}
                    priority='high'
                    classNames="ph__image"
                    imageProportions={{
                      width: '590',
                      height: '500',
                      sizes: '(min-width: 768px) 37vw, 590px'
                    }}
                  />
                </div>
                <svg className="ph__mask" fill="none" width="745" height="631" viewBox="0 0 745 631" ><path clip-rule="evenodd" d="m745 0h-745v631h745v-84.873c-6.832.001-13.667.034-20.502.101-197.657-.263-394.338 27.706-584.088 83.064l-138.41-527.465c233.147-66.6174 474.49-100.17111 716.962-99.67832h6.919c6.374-.0731 12.747-.12276 19.119-.14903z" fill="#6E273D" fill-rule="evenodd"/></svg>
              </div>}
            </div>
          </header>
        </>
      }
    </>
  ))
}


const HomeHeaderLayout = (currentPageData: PageData, state: State): Widget<Updater<State>> => {
  if (currentPageData.header.kind != "home") return nothing()

  const { image, linkPrimary, linkSecondary, pageIntro, pageTitle, grid } = currentPageData.header
  return fromJSX(setState => {
    return <>
      <header className='homeheader hh'>
        <div className='hh__outer ls-container--xl-nogut'>
          <div className="hh__inner ls-container--l">
            <div className='hh__content'>
              <div className='hh__content-inner'>
                {pageTitle && (
                  <h1 className='hh__title title--l title--bg' dangerouslySetInnerHTML={{ __html: unEntity(convertTitleForBackground(pageTitle)) }} />
                )}

                {pageIntro ? (
                  typeof pageIntro == 'string' ? (
                    <p className='hh__intro' dangerouslySetInnerHTML={{ __html: pageIntro }} />
                  ) : (
                    <p className='hh__intro'>{pageIntro}</p>
                  )
                ) : (
                  <></>
                )}

                {(linkPrimary || linkSecondary) ? (
                  <div className='hh__actions ls-clickables'>
                    {linkPrimary && (
                      <div className="hh__action ls-clickable">
                        <LinkLayout
                          link={linkPrimary}
                          className='hh__button button button--primary button--on-darkred'
                          onClick={() => {
                            pushDatalayer({
                              event: 'event.gtm',
                              category: 'pageheader-cta',
                              action: 'primary-' + linkPrimary.title,
                              label: window.location.href
                            })
                          }}
                        >
                          <span className='button__label'>{linkPrimary.title}</span>
                        </LinkLayout>
                      </div>
                    )}
                    {linkSecondary && (
                      <div className="hh__action ls-clickable">
                        <LinkLayout
                          link={linkSecondary}
                          className='hh__link button button--secondary button--on-darkred'
                          onClick={() => {
                            pushDatalayer({
                              event: 'event.gtm',
                              category: 'pageheader-cta',
                              action: 'secondary-' + linkSecondary.title,
                              label: window.location.href
                            })
                          }}
                        >
                          <span className="button__label">{linkSecondary.title}</span>
                        </LinkLayout>
                      </div>
                    )}
                  </div>
                ) : (
                  <></>
                )}
              </div>
            </div>

            <div className="hh__visual">
              <div className='hh__visual-inner fit-picture'>
                <SharedBlockLayout.ModernImageSrcSet
                  image={image}
                  lazyload={false}
                  priority='high'
                  classNames="hh__image"
                  imageProportions={{
                    width: '882',
                    height: '746',
                    sizes: '(min-width: 1440px) 878px, (min-width: 768px) 743px, 441px'
                  }}
                />
              </div>
              <svg className="hh__mask" fill="none" width="1053" height="637" viewBox="0 0 1053 637" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" clipRule="evenodd" d="M1052 -0.000976562H0.00195312V636.999H1052V570.911C944.82 555.474 836.343 548.19 727.5 549.249C529.843 548.986 333.162 576.956 143.411 632.313L5.00195 104.849C238.149 38.231 479.492 4.67738 721.964 5.17016H728.883C837.08 3.92944 944.99 9.43876 1052 21.5735V-0.000976562Z" fill="#6E273D"/></svg>
              <svg className="hh__swoosh" fill="none" height="1259" viewBox="0 0 2337 1259" width="2337" xmlns="http://www.w3.org/2000/svg"><path d="m734.2 5.25018c370.41-4.24759 737.46 70.61612 1076.63 219.58982 339.17 148.975 642.65 368.63 890.17 644.29l-364.02 384.87c-202.2-224.44-449.79-403.332-726.35-524.818s-575.77-182.791-877.813-179.853c-197.656-.263-394.338 27.707-584.088 83.064l-138.4097-527.464c233.1467-66.6179 474.4897-100.17159 716.9627-99.67882" fill="#d33350"/></svg>
            </div>
          </div>
        </div>

        {grid && (
          <section className="block block--grid block--grid-image">
            <LinkGridBlockLayout.Content
              items={grid}
              showPrimary={false}
              showSecondary={false}
            />
          </section>
        )}
      </header>
    </>
  })
}

export const renderPageHeader = (state: State, CMSLoaderState: AsyncState<CMSLoaderState>): Widget<Updater<State>> => {
  if (state.isCustomPage != 'none' && state[state.isCustomPage].header != undefined) {
    return state[state.isCustomPage].header!(state)
  }

  if (CMSLoaderState.kind != 'loaded') return nothing()

  const { pageData: currentPageData } = CMSLoaderState.value
  const { kind } = currentPageData.header

  switch (kind) {
    case 'home':
      return HomeHeaderLayout(currentPageData, state)
    case 'normal':
    case 'fullscreen':
      return PageHeaderLayout(currentPageData, state)
    default:
      return fromJSX((_) => <p>Not supported header</p>)
  }
}
